import { useApi } from '@src/app/context/Api';
import { configuration } from '@src/entities/Alert/includes/configuration';
import { AlertActions, SaveAlertPayload } from '@src/entities/Alert/includes/types';
import { useToast } from '@uhg-abyss/web/hooks/useToast';

type RefetchFn = () => void;

type UseSaveReturn = {
  handleSave: (payload?: SaveAlertPayload, refetch?: RefetchFn) => Promise<void>;
  mutation: unknown;
};

/**
 * Hook: useSaveAlert
 *
 * Custom React hook to handle saving an alert via API.
 * Shows toast notifications for info, success, and error states.
 * Clears the alert list cache and triggers a refetch on success.
 */
export const useSaveAlert = (action: AlertActions = 'edit'): UseSaveReturn => {
  const currentAction = configuration.messages?.[action];

  const { clearApiCache, useApiMutation } = useApi('Alert');
  const [SaveAlert, mutation] = useApiMutation('SaveAlert');

  const { toast } = useToast();

  /**
   * Handles saving the alert, showing toasts, and clearing cache.
   */
  const handleSave = async (payload: SaveAlertPayload, refetch: RefetchFn = () => {}): Promise<void> => {
    const toastId = `${action}-${configuration.slug}`;

    toast.show({
      autoClose: false,
      id: `${toastId}-info`,
      isLoading: true,
      message: currentAction?.info?.message,
      title: currentAction?.info?.title,
      variant: 'info',
    });

    await SaveAlert(payload, {
      onError: () => {
        toast.hide(`${toastId}-info`);
        toast.show({
          id: `${toastId}-error`,
          message: currentAction?.error?.message,
          title: currentAction?.error?.title,
          variant: 'error',
        });
      },
      onSuccess: async () => {
        toast.hide(`${toastId}-info`);
        toast.show({
          id: `${toastId}-success`,
          message: currentAction?.success?.message,
          title: currentAction?.success?.title,
          variant: 'success',
        });
        await clearApiCache([{ apiKey: 'Alert', queryKeys: ['ListAlerts'] }]);
        refetch();
      },
    });
  };

  return { handleSave, mutation };
};
